import { heroDelivery, heroPreloadLinks } from "@/lib/hero-delivery";
import { RichText } from "@/components/site/RichText";
import { LocaleLink, type PublicPath } from "@/components/site/LocaleLink";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/site/PageHero";
import { Section } from "@/components/site/Section";
import { Overline } from "@/components/site/Overline";
import { ServiceBand } from "@/components/site/ServiceBand";
import { Phone, Mail, MapPin, MessageCircle, ShieldCheck, Clock, ArrowRight, HeartHandshake } from "lucide-react";
import overOnsHero from "../../assets/over-ons-hero.avif";
import overOnsHeroSmall from "../../assets/over-ons-hero-640.avif";
import { FaqSection } from "@/components/site/FaqSection";
import { faqs, faqJsonLd } from "@/content/faqs";
import { addressLine, business, canonical, breadcrumbJsonLd, shareImageMeta, langPageMeta, responseTime } from "@/content/site";
import { publishedPageQuery } from "@/lib/content-queries";
import { pageContent } from "@/lib/page-content";
import { usePageContent, validateVoorvertoning } from "@/lib/use-page-content";
import { PhotoBlocks } from "@/components/site/PhotoBlocks";

const PATH = "/over-ons";
const SLEUTEL = "over-ons";

const HERO_FALLBACK = { src: overOnsHero, srcSet: `${overOnsHeroSmall} 640w, ${overOnsHero} 1024w` };

const CODE_META_TITLE = "Over Gopro Projects | Zwembadbouwer Maasmechelen";
const CODE_META_DESCRIPTION =
  "Gopro Projects uit Maasmechelen: bouwkundige zwembaden met 3 cm Recticel isolatie, polyester monoblock renovatie en zwembadtechniek in Limburg en Zuid-Nederland.";

export const Route = createFileRoute("/$lang/over-ons")({
  staticData: { sitemap: true },
  validateSearch: validateVoorvertoning,
  loader: async ({ context, params }) => ({
    page: await context.queryClient.ensureQueryData(publishedPageQuery(SLEUTEL, params.lang)),
  }),
  head: ({ params, loaderData }) => {
    const c = pageContent(loaderData?.page);
    const title = c.metaTitle(CODE_META_TITLE);
    const description = c.metaDescription(CODE_META_DESCRIPTION);
    const heroPhoto = heroDelivery(c.section("hero"), HERO_FALLBACK);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        {
          property: "og:description",
          content: "Zwembadbouwer uit Maasmechelen, gespecialiseerd in renovatie van polyester monoblock zwembaden.",
        },
        { property: "og:type", content: "website" },
        ...langPageMeta(params.lang, PATH).meta,
        { name: "twitter:card", content: "summary_large_image" },
        ...shareImageMeta("over-ons", "Gopro Projects, zwembadbouwer uit Maasmechelen"),
      ],
      links: [...langPageMeta(params.lang, PATH).links, ...heroPreloadLinks(heroPhoto)],
      scripts: [faqJsonLd(faqs.overOns), breadcrumbJsonLd("Over ons", PATH)],
    };
  },
  component: OverOnsPage,
});

const valueIcons = [ShieldCheck, Clock, MapPin, HeartHandshake];

const values = [
  {
    titel: "10 jaar garantie",
    tekst:
      "Op de structurele waterdichtheid van onze nieuwbouwkuipen en relining. Geen kleine lettertjes, wel duidelijke afspraken.",
  },
  {
    titel: `Reactie ${responseTime.within}`,
    tekst: "Of het nu een offerte of een lekkage is: wij laten u niet wachten. Snelheid en transparantie staan centraal.",
  },
  {
    titel: "Limburg & Zuid-Nederland",
    tekst:
      "Vanuit Maasmechelen werken wij in heel Limburg en in de Nederlandse grensstreek. Korte afstanden, dus snelle service.",
  },
  {
    titel: "Snel, correct en eerlijk",
    tekst:
      "Onze service in drie woorden: snelle reactie, correcte uitvoering en eerlijk advies. Zo bouwen wij vertrouwen.",
  },
];

const werkwijze = [
  "Vrijblijvend adviesgesprek en ter plaatse opname.",
  "Duidelijke offerte met planning en prijs.",
  "Uitvoering door eigen vakmensen en betrouwbare partners.",
  "Oplevering met uitleg en nazorg.",
];

function OverOnsPage() {
  const { lang } = Route.useParams();
  const { voorvertoning } = Route.useSearch();
  const c = usePageContent(SLEUTEL, lang, voorvertoning);
  const hero = c.section("hero");
  const heroPhoto = heroDelivery(hero, HERO_FALLBACK);
  const heroAlt = c.veld("hero", "alt", "Gopro Projects team aan het werk aan een zwembad");

  return (
    <div className="flex flex-col">
      <PageHero
        title={c.veld("hero", "titel", "Over Gopro Projects")}
        subtitle={c.rijkInline("hero",
          "Een familiaal bedrijf met passie voor zwembaden. Wij combineren jarenlange ervaring in nieuwbouw met een unieke specialisatie in renovatie.",
        )}
        imageSrc={heroPhoto.src}
        imageFocal={heroPhoto.focal}
        imageSrcSet={heroPhoto.srcSet}
        imageAlt={heroAlt}
        cta={{
          label: c.veld("hero", "knopTekst", "Neem contact op"),
          to: c.veld("hero", "knopLink", "/offerte") as PublicPath,
        }}
      />

      <Section tone="light">
        <div className="max-w-[70ch]">
          <Overline>{c.veld("verhaal", "overline", "Ons verhaal")}</Overline>
          <h2 className="mt-4 display-section">{c.veld("verhaal", "titel", "Ons verhaal")}</h2>
          {c
            .alineas("verhaal", [
              "Gopro Projects startte met één missie: zwembadbouw anders aanpakken. Niet alleen mooie baden maken, maar ook bestaande baden redden waar andere bedrijven stoppen.",
              "In de loop der jaren groeiden we uit tot een allround partner voor nieuwbouw, renovatie, techniek en afwerking. Onze niche? Polyester monoblock zwembaden renoveren — ongeveer de helft van onze activiteiten en een specialisme dat steeds zeldzamer wordt.",
              "Bij nieuwbouw isoleren wij elke kuip standaard met 3 cm Recticel en werken wij met kleurvaste polyester of vinylester bekleding. Vinylester is bovendien extra osmosebestendig. Van de eerste grondwerken tot de laatste boordsteen — of het bijhorend terras — regelen wij alles in eigen beheer.",
            ])
            .map((alinea, index) => (
              <p
                key={alinea.slice(0, 24)}
                className={`${index === 0 ? "mt-6" : "mt-4"} text-[17px] leading-7 text-on-surface-variant`}
              >
                <RichText waarde={alinea} inline />
              </p>
            ))}

          <h3 className="mt-10 font-display text-xl font-semibold">{c.veld("werkgebied", "titel", "Ons werkgebied")}</h3>
          <p className="mt-4 text-[17px] leading-7 text-on-surface-variant">
            {c.rijkInline("werkgebied",
              "Onze thuisbasis ligt in Maasmechelen. Van daaruit werken wij in heel Limburg — Genk, Hasselt, Bilzen, Lanaken, Dilsen-Stokkem, Bree, Tongeren, Sint-Truiden, Beringen en omgeving — en in de Nederlandse grensstreek rond Maastricht, Sittard-Geleen, Heerlen en Roermond. Die korte afstanden zijn een bewuste keuze: bij een lekkage of een dringende herstelling staan wij snel bij u.",
            )}
          </p>

          <h3 className="mt-10 font-display text-xl font-semibold">{c.veld("werkwijze", "titel", "Werkwijze")}</h3>
          <ol className="mt-4 list-decimal space-y-2 pl-5 text-[17px] leading-7 text-on-surface-variant">
            {c.lijst("werkwijze", werkwijze).map((stap) => (
              <li key={stap}>{stap}</li>
            ))}
          </ol>

          <h3 className="mt-10 font-display text-xl font-semibold">
            {c.veld("eerlijk", "titel", "Snel, correct en eerlijk")}
          </h3>
          <p className="mt-4 text-[17px] leading-7 text-on-surface-variant">
            {c.rijkInline("eerlijk",
              "Wij zeggen wat we doen en doen wat we zeggen. Is renoveren niet zinvol, dan zeggen wij dat ook — liever een eerlijk advies dan een ontevreden klant. Op de structurele waterdichtheid van onze nieuwbouwkuipen en relining geven wij 10 jaar garantie.",
            )}
          </p>
        </div>
      </Section>

      <ServiceBand
        items={c.kaarten("waarden", values).map((kaart, index) => ({
          icon: valueIcons[index % valueIcons.length]!,
          title: kaart.titel,
          text: kaart.tekst,
        }))}
      />

      <section className="grid bg-primary text-primary-foreground lg:grid-cols-2">
        <img
          src={hero?.mediaUrl ?? overOnsHero}
          alt={heroAlt}
          width={1024}
          height={768}
          loading="lazy"
          className="h-full min-h-96 w-full object-cover"
        />
        <div className="px-5 py-16 lg:px-16 lg:py-24">
          <Overline onDark>{c.veld("contact", "overline", "Contact")}</Overline>
          <h2 className="mt-4 display-section">{c.veld("contact", "titel", "Contactgegevens")}</h2>
          <p className="mt-6 max-w-[70ch] text-[17px] leading-7 text-primary-foreground/80">
            {c.rijkInline("contact", "Bereik ons telefonisch, via WhatsApp of e-mail. Wij staan voor u klaar.")}
          </p>
          <ul className="mt-8 divide-y divide-primary-foreground/20 border-y border-primary-foreground/20">
            <li className="py-4">
              <a href={`tel:${business.phoneE164}`} className="flex items-center gap-3 hover:text-secondary">
                <Phone className="h-5 w-5 text-secondary" strokeWidth={1.75} /> {business.phone}
              </a>
            </li>
            <li className="py-4">
              <a href="https://wa.me/32484976477" className="flex items-center gap-3 hover:text-secondary">
                <MessageCircle className="h-5 w-5 text-secondary" strokeWidth={1.75} /> WhatsApp
              </a>
            </li>
            <li className="py-4">
              <a href={`mailto:${business.email}`} className="flex items-center gap-3 hover:text-secondary">
                <Mail className="h-5 w-5 text-secondary" strokeWidth={1.75} /> {business.email}
              </a>
            </li>
            <li className="py-4">
              <span className="flex items-start gap-3">
                <MapPin className="mt-1 h-5 w-5 shrink-0 text-secondary" strokeWidth={1.75} />
                <span className="text-sm leading-6">
                  {addressLine}
                  <br />
                  Werkgebied: {business.areaLabel}
                  <br />
                  BTW {business.vat}
                </span>
              </span>
            </li>
          </ul>
          <div className="mt-10 border-t border-primary-foreground/20 pt-8">
            <Overline onDark>{c.veld("offerte", "overline", "Offerte")}</Overline>
            <h3 className="mt-4 font-display text-[clamp(1.5rem,3vw,1.75rem)] font-semibold">
              {c.veld("offerte", "titel", "Klaar voor uw project?")}
            </h3>
            <p className="mt-4 text-[17px] leading-7 text-primary-foreground/80">
              {c.rijkInline("offerte",
                `Vraag vandaag nog een vrijblijvende offerte of renovatie-diagnose aan. Wij contacteren u ${responseTime.within}.`,
              )}
            </p>
            <Button asChild size="lg" className="mt-8 bg-secondary text-secondary-foreground hover:bg-secondary/90">
              <LocaleLink to={c.veld("offerte", "knopLink", "/offerte") as PublicPath}>
                {c.veld("offerte", "knopTekst", "Offerte aanvragen")} <ArrowRight className="ml-2 h-4 w-4" />
              </LocaleLink>
            </Button>
          </div>
        </div>
      </section>
      <PhotoBlocks content={c} />
      <FaqSection items={faqs.overOns} />
    </div>
  );
}
